import {
  parseEther,
  readContract,
  writeContractAndWait,
  zeroAddress,
} from "../viem";

import { approveToken, getTokenAllowance } from ".";

import MafiaInventoryMarketplaceABI from "../../abis/MafiaInventoryMarketplace.json";

import config from "../../config/config";
import { GAME_CASH_ADDRESS } from "../../constants/const";
import { SwapToken } from "../../types/Contract/SwapToken";
import { delay } from "../../utils/utils";
import { isIndexingTxError } from "../parseTxError";

const address = config.marketAddress;
const abi = MafiaInventoryMarketplaceABI;

export const getSwapTokens = async () => {
  const swapTokenInfo: [SwapToken[], number[]] = await readContract({
    address,
    abi,
    functionName: "getSwapTokens",
  });

  return swapTokenInfo;
};

export const getListingInfo = async (listingId: number) => {
  const listingInfo = await readContract({
    address,
    abi,
    functionName: "getListingInfo",
    args: [listingId],
  });

  return listingInfo;
};

export const createListing = async (
  itemId: number,
  startingPrice: number,
  listingType: number,
  listingToken: string,
  duration: number,
  account: string
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "createListing",
    args: [
      itemId,
      parseEther(startingPrice.toString()),
      listingType,
      listingToken,
      duration,
    ],
    account,
  });

  return data;
};

export const purchaseFixedItem = async (
  listingId: number,
  swapTokenId: number,
  tokenAddress: string,
  tokenAmount: number,
  account: string
) => {
  if (tokenAddress === zeroAddress) {
    const nativeAmount = parseEther(tokenAmount.toFixed(18));

    const data = await writeContractAndWait({
      address,
      abi,
      functionName: "purchaseFixedItem",
      args: [listingId, swapTokenId],
      account,
      value: nativeAmount,
    });

    return data;
  } else if (tokenAddress === GAME_CASH_ADDRESS) {
    const data = await writeContractAndWait({
      address,
      abi,
      functionName: "purchaseFixedItem",
      args: [listingId, swapTokenId],
      account,
    });

    return data;
  } else {
    const tokenAllowance = await getTokenAllowance(
      tokenAddress,
      account,
      config.marketAddress
    );

    try {
      if (Number(tokenAllowance) < tokenAmount) {
        await approveToken(
          tokenAddress,
          tokenAmount,
          config.marketAddress,
          account
        );

        await delay(config.blockTime);
      }
    } catch (error: any) {
      if (isIndexingTxError(error)) {
        await delay(config.blockTime);
      } else {
        throw error;
      }
    }

    const data = await writeContractAndWait({
      address,
      abi,
      functionName: "purchaseFixedItem",
      args: [listingId, swapTokenId],
      account,
    });

    return data;
  }
};

export const bidOnAuctionItem = async (
  listingId: number,
  swapTokenId: number,
  price: number,
  tokenAddress: string,
  tokenAmount: number,
  account: string
) => {
  const newPrice = parseEther(price.toFixed(18));

  if (tokenAddress === zeroAddress) {
    const nativeAmount = parseEther(tokenAmount.toFixed(18));

    const data = await writeContractAndWait({
      address,
      abi,
      functionName: "bidOnAuctionItem",
      args: [listingId, swapTokenId, newPrice],
      account,
      value: nativeAmount,
    });

    return data;
  } else if (tokenAddress === GAME_CASH_ADDRESS) {
    const data = await writeContractAndWait({
      address,
      abi,
      functionName: "bidOnAuctionItem",
      args: [listingId, swapTokenId, newPrice],
      account,
    });

    return data;
  } else {
    const tokenAllowance = await getTokenAllowance(
      tokenAddress,
      account,
      config.marketAddress
    );

    try {
      if (Number(tokenAllowance) < tokenAmount) {
        await approveToken(
          tokenAddress,
          tokenAmount,
          config.marketAddress,
          account
        );

        await delay(config.blockTime);
      }
    } catch (error: any) {
      if (isIndexingTxError(error)) {
        await delay(config.blockTime);
      } else {
        throw error;
      }
    }

    const data = await writeContractAndWait({
      address,
      abi,
      functionName: "bidOnAuctionItem",
      args: [listingId, swapTokenId, newPrice],
      account,
    });

    return data;
  }
};

export const finishAuctionItem = async (listingId: number, account: string) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "finishAuctionItem",
    args: [listingId],
    account,
  });

  return data;
};

export const cancelListing = async (listingId: number, account: string) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "cancelListing",
    args: [listingId],
    account,
  });

  return data;
};
